import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';

import Bugsnag from '@bugsnag/js';
import { Form, FormField, Checkbox, StackLayout } from '@mc/wink';
import { getDestinationForField } from '../FormFieldManager/destinations';
import TextField from '../Field/BaseComponent/TextField';
import FieldContainer from '../Field/BaseComponent/FieldContainer';
import fieldLabel from '../Field/BaseComponent/fieldLabel';
import { getDefaultCountryProgram } from '../utils/helpers';
import MultiCountryDropdownAndFlagWrapper from './MultiCountryDropdownAndFlagWrapper';
import Field from './Field';
import FormFeedback from './FormFeedback';
import SubmitButton from './SubmitButton';

function ContactForm({
  description,
  destinations,
  fields,
  onSubmit,
  submitButtonAlignment,
  submitButtonBgHoverColor,
  submitButtonBgColor,
  submitButtonLabel,
  submitButtonTextColor,
  subscribeCheckboxLabel,
  isMultiCountryEnabled,
  formsMultiCountryFastFollowsEnabled,
  smsNotRequiredRemoveCountryCodeEnabled,
}) {
  const initialFormValues = {
    fields:
      fields?.reduce((map, field) => {
        map[field?.id] = '';
        return map;
      }, {}) || {},
    subscribe: false,
    'mc-SMSPHONE-ack': false,
  };
  let smsMergeFieldId = null;
  let legalText = {};
  let requiredLegalText = '';
  let optionalLegalText = '';
  let anchorMatches = [];

  const divRegex = /<\/?[div][^>]*>/gi;
  const fullAnchorRegex = /<a.*?<\/a>/g;
  const anchorRegex = /<a href="(.*?)" target="(.*?)">(.*?)<\/a>/;
  const smsField = destinations?.merge_field?.find(
    (field) => field.type === 'smsphone',
  );
  const [selectedCountry, setSelectedCountry] = useState('');
  const [helpText, setHelpText] = useState('');
  const [selectedLegalText, setSelectedLegalText] = useState({
    required: requiredLegalText || smsField?.legal?.required || '',
    optional: optionalLegalText || smsField?.legal?.optional || '',
  });
  const [isAnchorMatches, setIsAnchorMatches] = useState([]);
  const [currentPlaceholder, setCurrentPlaceholder] = useState('');

  const mockPlaceholders = [
    {
      countryCode: 'US',
      placeholder: '+1 000 000 0000',
      helpText: 'Include the US country code +1 before the phone number',
    },
    {
      countryCode: 'GB',
      placeholder: '+44 0000 000000',
      helpText: 'Include the GB country code +44 before the phone number',
    },
    {
      countryCode: 'CA',
      placeholder: '+1 000 000 0000',
      helpText: 'Include the CA country code +1 before the phone number',
    },
    {
      countryCode: 'AU',
      placeholder: '+61 000 000 000',
      helpText: 'Include the AU country code +61 before the phone number',
    },
    {
      countryCode: 'DE',
      placeholder: '+49 000 0000000',
      helpText: 'Fügen Sie vor der Telefonnummer die DE-Ländervorwahl +49 ein',
    },
    {
      countryCode: 'FR',
      placeholder: '+33 0 00 00 00 00',
      helpText: 'Incluez le code pays FR +33 avant le numéro de téléphone',
    },
    {
      countryCode: 'ES',
      placeholder: '+34 000 000 000',
      helpText: 'Incluya el código de país ES +34 antes del número de teléfono',
    },
    {
      countryCode: 'NL',
      placeholder: '+31 0 00000000',
      helpText: 'Voeg de NL-landcode +31 toe vóór het telefoonnummer',
    },
    {
      countryCode: 'BE',
      placeholder: '+32 000 00 00 00',
      helpText: 'Incluez le code pays BE +32 avant le numéro de téléphone',
    },
    {
      countryCode: 'CH',
      placeholder: '+41 00 000 00 00',
      helpText: 'Fügen Sie vor der Telefonnummer die CH-Ländervorwahl +41 ein',
    },
    {
      countryCode: 'AT',
      placeholder: '+43 000 000 0000',
      helpText: 'Fügen Sie vor der Telefonnummer die AT-Ländervorwahl +43 ein',
    },
    {
      countryCode: 'IE',
      placeholder: '+353 00 000 0000',
      helpText: 'Include the IE country code +353 before the phone number',
    },
    {
      countryCode: 'IT',
      placeholder: '+39 000 000 0000',
      helpText:
        'Includere il prefisso internazionale IT +39 prima del numero di telefono',
    },
  ];

  // Mock placeholders for different countries
  const getDefaultPlaceholder = (countryCode) => {
    const selectedPlaceholder = mockPlaceholders.find(
      (item) => item.countryCode === countryCode,
    );
    return selectedPlaceholder
      ? selectedPlaceholder.placeholder
      : mockPlaceholders[0].placeholder;
  };

  const getDefaultHelpText = (countryCode) => {
    const selectedHelpText = mockPlaceholders.find(
      (item) => item.countryCode === countryCode,
    );
    return selectedHelpText
      ? selectedHelpText.helpText
      : mockPlaceholders[0].helpText;
  };

  const setSmsLegalText = useCallback(() => {
    if (!smsField || !smsField.legal) {
      return;
    }

    const smsLegalText = smsField.legal;

    const countryProgram = smsField.smsProgramData?.find(
      (program) => program?.countryCode === selectedCountry,
    );

    if (countryProgram) {
      const requiredLegal =
        countryProgram.requiredTemplate || smsLegalText.required;
      const optionalLegal =
        countryProgram.optionalTemplate || smsLegalText.optional;

      const smsRequiredLegalText = requiredLegal
        ?.replace(divRegex, '')
        ?.replace(fullAnchorRegex, '')
        ?.slice(0, -1);
      const smsOptionalLegalText = optionalLegal
        ?.replace(divRegex, '')
        ?.replace(fullAnchorRegex, '')
        ?.slice(0, -1);

      const smsAnchorMatches = requiredLegal?.match(anchorRegex);
      if (!smsAnchorMatches) {
        return '';
      }
      setIsAnchorMatches(smsAnchorMatches);
      setSelectedLegalText({
        required: smsRequiredLegalText,
        optional: smsOptionalLegalText,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCountry]);

  destinations.merge_field.some((field) => {
    if (field?.type === 'smsphone') {
      smsMergeFieldId = field?.id;
      legalText = field?.legal;

      if (!isMultiCountryEnabled) {
        requiredLegalText = legalText?.required
          ?.replace(divRegex, '')
          ?.replace(fullAnchorRegex, '')
          ?.slice(0, -1);
        optionalLegalText = legalText?.optional
          ?.replace(divRegex, '')
          ?.replace(fullAnchorRegex, '')
          ?.slice(0, -1);
        anchorMatches = legalText?.required?.match(anchorRegex); //the anchor stays the same for required and optional
        if (!anchorMatches) {
          return '';
        }
      }
      return true;
    }
    return false;
  });

  const formRef = useRef();

  // (On Load) Function to prepend the country calling code to the phone number field
  const prependPrefix = useCallback(
    (smsProgramData) => {
      const smsDestinationField = fields?.find(
        (field) => field?.destination?.id === smsMergeFieldId,
      );

      const shouldAppendCountryCode = smsNotRequiredRemoveCountryCodeEnabled
        ? smsDestinationField?.isRequired
        : true;

      for (const key in initialFormValues?.fields) {
        if (key === smsDestinationField?.id?.toString()) {
          initialFormValues.fields[key] = shouldAppendCountryCode
            ? smsProgramData?.countryCallingCode || ''
            : '';
          break;
        }
      }
    },
    [
      fields,
      initialFormValues,
      smsMergeFieldId,
      smsNotRequiredRemoveCountryCodeEnabled,
    ],
  );

  // (On Change) Function to prepend the country calling code to the phone number field
  const prependPrefixOnCountryChange = useCallback(
    (selectedCountryCode) => {
      const smsDestinationField = fields?.find(
        (field) => field?.destination?.id === smsMergeFieldId,
      );

      const shouldAppendCountryCode = smsNotRequiredRemoveCountryCodeEnabled
        ? smsDestinationField?.isRequired
        : true;

      const program = smsField?.smsProgramData?.find(
        (p) => p?.countryCode === selectedCountryCode,
      );

      for (const key in initialFormValues?.fields) {
        if (key === smsDestinationField?.id?.toString()) {
          if (formRef && formRef.current) {
            formRef.current.setValue(`fields.${key}`)(
              shouldAppendCountryCode ? program?.countryCallingCode || '' : '',
            );
          }
          break;
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      fields,
      initialFormValues.fields,
      smsMergeFieldId,
      smsNotRequiredRemoveCountryCodeEnabled,
    ],
  );

  // Runs only on initial load ans setting the default country
  useEffect(() => {
    if (!isMultiCountryEnabled) {
      return;
    }
    const defaultCountry = getDefaultCountryProgram(
      destinations?.merge_field?.find((field) => field?.type === 'smsphone')
        ?.defaultCountryCode,
      destinations.merge_field?.find((field) => field?.type === 'smsphone')
        ?.smsProgramData,
    );
    const countryCode = defaultCountry?.countryCode || '';
    setSelectedCountry(countryCode);
    setHelpText(getDefaultHelpText(countryCode));
    setCurrentPlaceholder(getDefaultPlaceholder(countryCode));

    if (formsMultiCountryFastFollowsEnabled) {
      prependPrefix(defaultCountry);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMultiCountryEnabled, formsMultiCountryFastFollowsEnabled]);

  // Runs when the country is changed from the dropdown
  useEffect(() => {
    if (!isMultiCountryEnabled) {
      return;
    }
    if (selectedCountry) {
      setSmsLegalText();
      setCurrentPlaceholder(getDefaultPlaceholder(selectedCountry));

      if (smsField) {
        const program = smsField?.smsProgramData?.find(
          (p) => p?.countryCode === selectedCountry,
        );
        setHelpText(getDefaultHelpText(selectedCountry));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCountry, isMultiCountryEnabled]);

  const handleOnCountryChange = (country) => {
    setSelectedCountry(country);
    setCurrentPlaceholder(getDefaultPlaceholder(country));

    setHelpText(getDefaultHelpText(selectedCountry));

    setSelectedLegalText({
      required: requiredLegalText,
      optional: optionalLegalText,
    });
  };

  return (
    <Form onSubmit={onSubmit} initialValues={initialFormValues} ref={formRef}>
      {description && (
        // This is user generated, but we're stripping it on save
        // eslint-disable-next-line react/no-danger
        <StackLayout dangerouslySetInnerHTML={{ __html: description }} />
      )}
      {fields?.map((field) => {
        // If the field or destination is missing, fail to render the field
        // but let the page render still
        try {
          if (!field) {
            throw new Error(`Field ${field} is missing`);
          }

          const destination = getDestinationForField(field, destinations);

          if (field?.destination?.id === smsMergeFieldId) {
            return (
              <FieldContainer key={field.id}>
                <FormField
                  name={`fields.${field.id}`}
                  component={(propsFromForm, ...props) => {
                    return (
                      <TextField
                        field={field}
                        label={fieldLabel(field, destination)}
                        propsFromForm={propsFromForm}
                        textClassName="contact-form-field"
                        type="tel"
                        placeholder={
                          isMultiCountryEnabled ? currentPlaceholder : undefined
                        }
                        prefixText={
                          isMultiCountryEnabled ? (
                            <MultiCountryDropdownAndFlagWrapper
                              field={field}
                              destination={destination}
                              selectedCountry={selectedCountry}
                              onCountryChange={(country) => {
                                handleOnCountryChange(country);

                                if (formsMultiCountryFastFollowsEnabled) {
                                  prependPrefixOnCountryChange(country);
                                }
                              }}
                              label={fieldLabel(field, destination)}
                              hideLabel={true}
                            />
                          ) : undefined
                        }
                        {...props}
                      />
                    );
                  }}
                  label={fieldLabel(field, destination)}
                />
                {isMultiCountryEnabled &&
                  !formsMultiCountryFastFollowsEnabled && (
                    <div>
                      <span
                        style={{ color: '#4C555B', fontSize: '12px' }}
                        id="help-text"
                      >
                        {helpText}
                      </span>
                    </div>
                  )}
                <div
                  className="mc-sms-phone-group"
                  style={{
                    display: 'flex',
                    gap: '15px',
                    marginTop: '12px',
                  }}
                >
                  {field?.isRequired && (
                    <FormField
                      name="mc-SMSPHONE-ack"
                      component={Checkbox}
                      aria-labelledby="sms-legal-text"
                    />
                  )}
                  {isMultiCountryEnabled ? (
                    <div id="sms-legal-text">
                      {field?.isRequired
                        ? selectedLegalText?.required
                        : selectedLegalText?.optional}
                      {isAnchorMatches && isAnchorMatches.length > 0 && (
                        <>
                          &nbsp;
                          <a
                            href={isAnchorMatches?.[1]}
                            target={isAnchorMatches?.[2]}
                          >
                            {isAnchorMatches?.[3]}
                          </a>
                          .
                        </>
                      )}
                    </div>
                  ) : (
                    <div id="sms-legal-text">
                      {field?.isRequired
                        ? requiredLegalText
                        : optionalLegalText}
                      {anchorMatches && anchorMatches.length > 0 && (
                        <>
                          &nbsp;
                          <a
                            href={anchorMatches?.[1]}
                            target={anchorMatches?.[2]}
                          >
                            {anchorMatches?.[3]}
                          </a>
                          .
                        </>
                      )}
                    </div>
                  )}
                </div>
              </FieldContainer>
            );
          }

          return (
            <Field
              field={field}
              key={field.id}
              destination={destination}
              formsMultiCountryFastFollowsEnabled={
                formsMultiCountryFastFollowsEnabled
              }
              smsNotRequiredRemoveCountryCodeEnabled={
                smsNotRequiredRemoveCountryCodeEnabled
              }
            />
          );
        } catch (error) {
          Bugsnag.notify(error);
          return null;
        }
      })}
      <FormField
        label={subscribeCheckboxLabel}
        name="subscribe"
        component={Checkbox}
      />
      <FormFeedback />
      <SubmitButton
        alignment={submitButtonAlignment}
        bgHoverColor={submitButtonBgHoverColor}
        bgColor={submitButtonBgColor}
        label={submitButtonLabel}
        textColor={submitButtonTextColor}
      />
    </Form>
  );
}

ContactForm.propTypes = {
  description: PropTypes.string,
  destinations: PropTypes.object.isRequired,
  fields: PropTypes.array.isRequired,
  formsMultiCountryFastFollowsEnabled: PropTypes.bool,
  isMultiCountryEnabled: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  smsNotRequiredRemoveCountryCodeEnabled: PropTypes.bool,
  submitButtonAlignment: PropTypes.string,
  submitButtonBgColor: PropTypes.string,
  submitButtonBgHoverColor: PropTypes.string,
  submitButtonLabel: PropTypes.string,
  submitButtonTextColor: PropTypes.string,
  subscribeCheckboxLabel: PropTypes.string,
};

export default ContactForm;
